import React, { useState, useEffect } from 'react';

interface HistoryEntry {
  timestamp: string;
  type: 'command' | 'interactive';
  request: any;
  response: any;
  status: number;
}

export default function SlackTestIterableV1() {
  const [command, setCommand] = useState('/apprva');
  const [question, setQuestion] = useState('Should we deploy to production?');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState<number | null>(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [testMode, setTestMode] = useState<'direct' | 'webhook' | 'message'>('direct');
  const [slackChannel, setSlackChannel] = useState('C09H9EC5W7N');
  const [slackMessage, setSlackMessage] = useState('🤖 Test message from Slack Tester!');

  // Load history from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('slack-test-history');
    if (saved) {
      setHistory(JSON.parse(saved));
    }
  }, []);

  // Save history to localStorage
  useEffect(() => {
    localStorage.setItem('slack-test-history', JSON.stringify(history));
  }, [history]);

  const testConnection = async () => {
    setLoading(true);
    setResponse('');

    try {
      setResponse('=== RUNNING DIAGNOSTICS ===\n\n');

      // Test 1: Health Check
      setResponse(prev => prev + '[1] System Health Check...\n');
      const healthRes = await fetch('https://futr-bet-api.b-9f2.workers.dev/test/health')
        .then(r => r.json())
        .catch(err => ({ error: err.message }));

      if (!healthRes.error) {
        setResponse(prev => prev + `✓ System Status: ${healthRes.status}\n`);
        setResponse(prev => prev + `  - Database: ${healthRes.checks.database ? '✓' : '✗'}\n`);
        setResponse(prev => prev + `  - Slack: ${healthRes.checks.slack ? '✓' : '✗'}\n`);
        setResponse(prev => prev + `  - AI: ${healthRes.checks.ai ? '✓' : '✗'}\n`);
        setResponse(prev => prev + `  - CORS: ${healthRes.checks.cors ? '✓' : '✗'}\n\n`);
      } else {
        setResponse(prev => prev + `✗ Health check FAILED: ${healthRes.error}\n\n`);
      }

      // Test 2: Echo Test
      setResponse(prev => prev + '[2] Testing Echo Endpoint...\n');
      const echoRes = await fetch('https://futr-bet-api.b-9f2.workers.dev/test/echo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test: 'echo', timestamp: Date.now() })
      })
        .then(r => r.json())
        .catch(err => ({ error: err.message }));

      if (!echoRes.error && echoRes.success) {
        setResponse(prev => prev + `✓ Echo test OK\n`);
        setResponse(prev => prev + `  - Received content-type: ${echoRes.received.contentType}\n\n`);
      } else {
        setResponse(prev => prev + `✗ Echo test FAILED: ${echoRes.error || 'Unknown'}\n\n`);
      }

      // Test 3: Validate Slack Format
      setResponse(prev => prev + '[3] Testing Slack Payload Validation...\n');

      // Send proper URL-encoded form data
      const formData = new URLSearchParams();
      formData.append('command', '/test');
      formData.append('text', 'test message');
      formData.append('user_id', 'U123456');
      formData.append('channel_id', 'C09H9EC5W7N');

      const validateRes = await fetch('https://futr-bet-api.b-9f2.workers.dev/test/validate-slack', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString()
      })
        .then(r => r.json())
        .catch(err => ({ error: err.message }));

      if (!validateRes.error) {
        if (validateRes.valid) {
          setResponse(prev => prev + `✓ Slack payload validation PASSED\n\n`);
        } else {
          setResponse(prev => prev + `✗ Validation FAILED:\n`);
          validateRes.validation.errors.forEach(err => {
            setResponse(prev => prev + `  - ${err}\n`);
          });
          setResponse(prev => prev + '\n');
        }
      } else {
        setResponse(prev => prev + `✗ Validation test FAILED: ${validateRes.error}\n\n`);
      }

      // Test 4: Actual Slack Endpoint
      setResponse(prev => prev + '[4] Testing Slack Events Endpoint...\n');
      const slackRes = await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString()
      })
        .then(async r => ({
          ok: r.ok,
          status: r.status,
          data: await r.text()
        }))
        .catch(err => ({ error: err.message }));

      if (!slackRes.error && slackRes.ok) {
        setResponse(prev => prev + `✓ Slack endpoint OK (${slackRes.status})\n\n`);
      } else {
        setResponse(prev => prev + `✗ Slack endpoint FAILED: ${slackRes.error || slackRes.data}\n\n`);
      }

      setResponse(prev => prev + '=== DIAGNOSTICS COMPLETE ===\n');

    } catch (error) {
      setResponse(prev => prev + `\nFATAL ERROR: ${error.message}\n${error.stack}`);
    } finally {
      setLoading(false);
    }
  };

  const sendToSlack = async () => {
    setLoading(true);
    setResponse('');
    const timestamp = new Date().toISOString();

    const requestPayload = {
      command,
      text: question,
      user_name: 'test_user',
      user_id: 'U123456',
      channel_id: 'C09H9EC5W7N',
      team_id: 'T123456',
      response_url: webhookUrl || 'https://hooks.slack.com/commands/1234/5678'
    };

    try {
      const endpoint = testMode === 'webhook' && webhookUrl
        ? webhookUrl
        : 'https://futr-bet-api.b-9f2.workers.dev/slack/events';

      setResponse(`Sending to: ${endpoint}\n\nPayload:\n${JSON.stringify(requestPayload, null, 2)}\n\n`);

      // Slack expects URL-encoded form data, not JSON!
      const formData = new URLSearchParams();
      Object.keys(requestPayload).forEach(key => {
        formData.append(key, requestPayload[key]);
      });

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      const responseText = await res.text();
      let data;
      try {
        data = JSON.parse(responseText);
        setResponse(prev => prev + `Status: ${res.status}\n\nResponse:\n${JSON.stringify(data, null, 2)}`);
      } catch {
        setResponse(prev => prev + `Status: ${res.status}\n\nRaw Response:\n${responseText}`);
        data = { raw: responseText };
      }

      // Add to history
      const entry: HistoryEntry = {
        timestamp,
        type: 'command',
        request: requestPayload,
        response: data,
        status: res.status
      };
      setHistory([entry, ...history.slice(0, 49)]); // Keep last 50
    } catch (error) {
      setResponse(`ERROR: ${error.message}\n\nStack:\n${error.stack}`);
    } finally {
      setLoading(false);
    }
  };

  const testInteractive = async (action: string) => {
    setLoading(true);

    try {
      const res = await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/interactive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'block_actions',
          user: { id: 'test_user', name: 'Test User' },
          actions: [{
            action_id: `${action}_action`,
            value: action,
            text: { text: action }
          }],
          response_url: 'https://test.slack.com/response'
        })
      });

      const data = await res.json();
      setResponse(JSON.stringify(data, null, 2));
    } catch (error) {
      setResponse(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('slack-test-history');
  };

  const loadHistoryEntry = (index: number) => {
    const entry = history[index];
    if (entry.type === 'command') {
      setCommand(entry.request.command);
      setQuestion(entry.request.text);
      setResponse(JSON.stringify(entry.response, null, 2));
    }
    setSelectedHistoryIndex(index);
  };

  const saveScenario = () => {
    const scenario = { command, question, webhookUrl };
    const scenarios = JSON.parse(localStorage.getItem('slack-test-scenarios') || '[]');
    scenarios.push({ ...scenario, saved: new Date().toISOString() });
    localStorage.setItem('slack-test-scenarios', JSON.stringify(scenarios));
    alert('SCENARIO SAVED');
  };

  const sendMessageToSlack = async () => {
    setLoading(true);
    setResponse('');
    const timestamp = new Date().toISOString();

    try {
      const res = await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: slackMessage,
          channel: slackChannel
        })
      });

      const data = await res.json();
      setResponse(JSON.stringify(data, null, 2));

      // Add to history
      const entry: HistoryEntry = {
        timestamp,
        type: 'command',
        request: { text: slackMessage, channel: slackChannel },
        response: data,
        status: res.status
      };
      setHistory([entry, ...history.slice(0, 49)]);
    } catch (error) {
      setResponse(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono p-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl mb-8">SLACK TEST v1.1.0 [ENHANCED]</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* LEFT COLUMN - Controls */}
          <div>
            {/* Command Builder */}
            <div className="border border-green-400 p-4 mb-4">
              <div className="flex justify-between items-center mb-4">
                <span className="text-yellow-400">◼ COMMAND BUILDER</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setTestMode('direct')}
                    className={`px-2 py-1 border ${testMode === 'direct' ? 'border-green-400 bg-green-400 text-black' : 'border-gray-600 text-gray-600'}`}
                  >
                    DIRECT
                  </button>
                  <button
                    onClick={() => setTestMode('webhook')}
                    className={`px-2 py-1 border ${testMode === 'webhook' ? 'border-green-400 bg-green-400 text-black' : 'border-gray-600 text-gray-600'}`}
                  >
                    WEBHOOK
                  </button>
                  <button
                    onClick={() => setTestMode('message')}
                    className={`px-2 py-1 border ${testMode === 'message' ? 'border-green-400 bg-green-400 text-black' : 'border-gray-600 text-gray-600'}`}
                  >
                    MESSAGE
                  </button>
                </div>
              </div>

              {testMode === 'webhook' && (
                <div className="mb-4">
                  <label className="block mb-2 text-yellow-400">WEBHOOK URL:</label>
                  <input
                    type="text"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    className="w-full bg-black border border-yellow-400 p-2 text-yellow-400"
                    placeholder="https://hooks.slack.com/services/..."
                  />
                </div>
              )}

              {testMode === 'message' && (
                <>
                  <div className="mb-4">
                    <label className="block mb-2 text-cyan-400">CHANNEL ID:</label>
                    <input
                      type="text"
                      value={slackChannel}
                      onChange={(e) => setSlackChannel(e.target.value)}
                      className="w-full bg-black border border-cyan-400 p-2 text-cyan-400"
                      placeholder="C09H9EC5W7N"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block mb-2 text-cyan-400">MESSAGE:</label>
                    <textarea
                      value={slackMessage}
                      onChange={(e) => setSlackMessage(e.target.value)}
                      className="w-full bg-black border border-cyan-400 p-2 text-cyan-400 h-20 resize-none"
                      placeholder="Your message to send to Slack..."
                    />
                  </div>
                </>
              )}

              {testMode !== 'message' && (
                <>
                  <div className="mb-4">
                    <label className="block mb-2">COMMAND:</label>
                    <select
                      value={command}
                      onChange={(e) => setCommand(e.target.value)}
                      className="w-full bg-black border border-green-400 p-2 text-green-400"
                    >
                      <option value="/apprva">/apprva</option>
                      <option value="/sh11">/sh11</option>
                      <option value="/2rh140">/2rh140</option>
                      <option value="/test">/test</option>
                      <option value="/custom">/custom</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block mb-2">QUESTION:</label>
                    <textarea
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      className="w-full bg-black border border-green-400 p-2 text-green-400 h-20 resize-none"
                      placeholder="Enter your question"
                    />
                  </div>
                </>
              )}

              <div className="flex gap-2">
                <button
                  onClick={testMode === 'message' ? sendMessageToSlack : sendToSlack}
                  disabled={loading}
                  className="bg-green-400 text-black px-4 py-2 hover:bg-green-500 disabled:opacity-50 flex-1"
                >
                  {loading ? 'SENDING...' : testMode === 'message' ? 'SEND MESSAGE' : 'SEND TO SLACK'}
                </button>
                {testMode !== 'message' && (
                  <button
                    onClick={saveScenario}
                    className="border border-cyan-400 text-cyan-400 px-4 py-2 hover:bg-cyan-400 hover:text-black"
                  >
                    SAVE
                  </button>
                )}
              </div>
            </div>

            {/* Interactive Buttons Test */}
            <div className="border border-green-400 p-4 mb-4">
              <div className="mb-2 text-yellow-400">◼ INTERACTIVE BUTTONS:</div>
              <div className="flex gap-2">
                <button
                  onClick={() => testInteractive('approve')}
                  disabled={loading}
                  className="border border-green-400 px-4 py-2 hover:bg-green-400 hover:text-black disabled:opacity-50 flex-1"
                >
                  APPROVE
                </button>
                <button
                  onClick={() => testInteractive('maybe')}
                  disabled={loading}
                  className="border border-yellow-400 text-yellow-400 px-4 py-2 hover:bg-yellow-400 hover:text-black disabled:opacity-50 flex-1"
                >
                  MAYBE
                </button>
                <button
                  onClick={() => testInteractive('reject')}
                  disabled={loading}
                  className="border border-red-400 text-red-400 px-4 py-2 hover:bg-red-400 hover:text-black disabled:opacity-50 flex-1"
                >
                  REJECT
                </button>
              </div>
            </div>

            {/* History */}
            <div className="border border-cyan-400 p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-cyan-400">◼ HISTORY [{history.length}]</span>
                <button
                  onClick={clearHistory}
                  className="text-red-400 text-xs hover:bg-red-400 hover:text-black px-2 py-1"
                >
                  CLEAR
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {history.length === 0 ? (
                  <div className="text-gray-600 text-xs">// No history yet</div>
                ) : (
                  history.map((entry, idx) => (
                    <div
                      key={idx}
                      onClick={() => loadHistoryEntry(idx)}
                      className={`cursor-pointer p-2 mb-1 border text-xs hover:bg-green-400 hover:text-black ${
                        selectedHistoryIndex === idx
                          ? 'border-yellow-400 bg-yellow-400 text-black'
                          : 'border-gray-700'
                      }`}
                    >
                      <div className="flex justify-between">
                        <span>{entry.type.toUpperCase()}</span>
                        <span className={entry.status === 200 ? 'text-green-400' : 'text-red-400'}>
                          {entry.status}
                        </span>
                      </div>
                      <div className="text-gray-400 truncate">
                        {entry.type === 'command' ? entry.request.text : entry.request.actions?.[0]?.value}
                      </div>
                      <div className="text-gray-600">
                        {new Date(entry.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN - Response & Inspector */}
          <div>
            {/* Raw Response */}
            <div className="border border-green-400 p-4 mb-4 relative">
              <div className="flex justify-between items-center mb-2">
                <span className="text-yellow-400">◼ RESPONSE:</span>
                {response && (
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(response);
                      alert('COPIED TO CLIPBOARD');
                    }}
                    className="text-cyan-400 text-xs border border-cyan-400 px-2 py-1 hover:bg-cyan-400 hover:text-black"
                  >
                    COPY
                  </button>
                )}
              </div>
              <pre className="overflow-x-auto text-xs h-64 overflow-y-auto">
                {response || '// No response yet'}
              </pre>
            </div>

            {/* Request Inspector */}
            {selectedHistoryIndex !== null && history[selectedHistoryIndex] && (
              <div className="border border-purple-400 p-4 mb-4">
                <div className="mb-2 text-purple-400">◼ REQUEST INSPECTOR:</div>
                <pre className="overflow-x-auto text-xs h-48 overflow-y-auto text-purple-300">
                  {JSON.stringify(history[selectedHistoryIndex].request, null, 2)}
                </pre>
              </div>
            )}

            {/* Status */}
            <div className="border border-gray-700 p-4">
              <div className="text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-500">STATUS:</span>
                  <span className={loading ? 'text-yellow-400' : 'text-green-400'}>
                    {loading ? 'PROCESSING' : 'READY'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">MODE:</span>
                  <span className="text-cyan-400">{testMode.toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">ENDPOINT:</span>
                  <span className="text-gray-400">futr-bet-api.b-9f2.workers.dev</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">VERSION:</span>
                  <span className="text-gray-400">1.2.0-diagnostic</span>
                </div>
              </div>
              <button
                onClick={testConnection}
                disabled={loading}
                className="w-full mt-4 border border-red-400 text-red-400 px-2 py-1 hover:bg-red-400 hover:text-black disabled:opacity-50"
              >
                TEST CONNECTION
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}